import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { selectDateData } from '../../../../services/Columns';
import { dateFormatter, disabledEndDate } from '../../../../utils';
import { CommonButton } from '../../../../components/UIElements/Button';
import DatePicker from '../../../../components/UIElements/DatePicker';
import Select from '../../../../components/UIElements/Select';
import {
  FormElementWrapper,
  FormWrapper,
} from '../../../../components/Wrappers';
import { MONTH_DATE_YEAR, WEEK_START_FROM_DAY } from '../../../../config';

const paymentTypeOptions = [
  { value: '', name: 'All Payment Types' },
  { value: 'card', name: 'Card' },
  { value: 'cash', name: 'Cash' },
  { value: 'check', name: 'Check' },
  { value: 'finance', name: 'Finance' },
];

const deductionStatusOptions = [
  { value: '', name: 'All Deduction Statuses' },
  { value: 'pending', name: 'Pending' },
  { value: 'partial', name: 'Partial' },
  { value: 'completed', name: 'Completed' },
];

function RefundsAndDeductionsFilterForm({
  onSubmit,
  onReset,
  setValues,
  filterValue,
  values,
}) {
  const { t } = useTranslation();

  const handleLink = (defaultKey) => {
    let todayDate = new Date();
    if (defaultKey) {
      if (defaultKey === 'today') {
        const data = {
          dateType: 'today',
          startDate: dateFormatter(todayDate, 'YYYY-MM-DD'),
          endDate: dateFormatter(todayDate, 'YYYY-MM-DD'),
        };
        setValues({ ...values, ...data });
      }
      if (defaultKey === 'yesterday') {
        const data = {
          dateType: 'yesterday',
          startDate: dateFormatter(moment().subtract(1, 'days'), 'YYYY-MM-DD'),
          endDate: dateFormatter(moment().subtract(1, 'days'), 'YYYY-MM-DD'),
        };
        setValues({ ...values, ...data });
      }
      if (defaultKey === 'currentWeek') {
        const startOfWeek = moment()
          .startOf('week')
          .add(WEEK_START_FROM_DAY || 0, 'days');
        const endOfWeek = moment()
          .endOf('week')
          .add(WEEK_START_FROM_DAY || 0, 'days');
        const data = {
          dateType: 'currentWeek',
          startDate: dateFormatter(startOfWeek, 'YYYY-MM-DD'),
          endDate: dateFormatter(endOfWeek, 'YYYY-MM-DD'),
        };
        setValues({ ...values, ...data });
      }
      if (defaultKey === 'previousWeek') {
        const prev = moment()
          .startOf('week')
          .subtract(1, 'weeks')
          .add(WEEK_START_FROM_DAY || 0, 'days');
        const end = moment()
          .startOf('week')
          .subtract(1, 'days')
          .add(WEEK_START_FROM_DAY || 0, 'days');
        const data = {
          dateType: 'previousWeek',
          startDate: dateFormatter(prev, 'YYYY-MM-DD'),
          endDate: dateFormatter(end, 'YYYY-MM-DD'),
        };
        setValues({ ...values, ...data });
      }
      if (defaultKey === 'currentMonth') {
        const prev = moment().startOf('month');
        const end = moment().endOf('month');
        const data = {
          dateType: 'currentMonth',
          startDate: dateFormatter(prev, 'YYYY-MM-DD'),
          endDate: dateFormatter(end, 'YYYY-MM-DD'),
        };
        setValues({ ...values, ...data });
      }
      if (defaultKey === 'previousMonth') {
        const prev = moment().startOf('month').subtract(1, 'month');
        const end = moment().startOf('month').subtract(1, 'days');
        const data = {
          dateType: 'previousMonth',
          startDate: dateFormatter(prev, 'YYYY-MM-DD'),
          endDate: dateFormatter(end, 'YYYY-MM-DD'),
        };
        setValues({ ...values, ...data });
      }
      if (defaultKey === 'currentYear') {
        const prev = moment().startOf('year');
        const end = moment().endOf('year');
        const data = {
          dateType: 'currentYear',
          startDate: dateFormatter(prev, 'YYYY-MM-DD'),
          endDate: dateFormatter(end, 'YYYY-MM-DD'),
        };
        setValues({ ...values, ...data });
      }
      if (defaultKey === 'previousYear') {
        const prev = moment().startOf('year').subtract(1, 'year');
        const end = moment().startOf('year').subtract(1, 'days');
        const data = {
          dateType: 'previousYear',
          startDate: dateFormatter(prev, 'YYYY-MM-DD'),
          endDate: dateFormatter(end, 'YYYY-MM-DD'),
        };
        setValues({ ...values, ...data });
      }
    }
  };

  return (
    <FormWrapper
      initialValues={{
        dateType: values?.dateType,
        startDate: values?.startDate,
        endDate: values?.endDate,
        paymentType: values?.paymentType || '',
        deductionStatus: values?.deductionStatus || '',
      }}
      onSubmit={onSubmit}
      row={true}
      extraClassName="g-3 align-items-end"
      enableReinitialize={true}
    >
      {(props) => (
        <>
          {/* Quick Date Presets */}
          <FormElementWrapper
            sm={12}
            md={6}
            xl={2}
            element={() => (
              <Select
                placeholder={t('text.truckOrdering.selectByDate')}
                onSelect={(e) => {
                  handleLink(e);
                }}
                formik={props}
                options={selectDateData}
                name="dateType"
              />
            )}
          />

          {/* Start Date */}
          <FormElementWrapper
            sm={12}
            md={6}
            xl={2}
            element={() => (
              <DatePicker
                name="startDate"
                className="form-control date-picker shadow-none"
                placeholder={t('text.common.fromDate')}
                formik={props}
                format={MONTH_DATE_YEAR}
                onChange={(e) => {
                  setValues({
                    ...values,
                    dateType: undefined,
                    startDate: e?.format('YYYY-MM-DD'),
                    endDate: undefined,
                  });
                }}
                disabledDate={(current) => {
                  return (
                    props?.values?.endDate &&
                    current >
                      moment(props.values.endDate, 'YYYY-MM-DD').endOf('day')
                  );
                }}
              />
            )}
          />

          {/* End Date */}
          <FormElementWrapper
            sm={12}
            md={6}
            xl={2}
            element={() => (
              <DatePicker
                name="endDate"
                className="form-control date-picker shadow-none"
                placeholder={t('text.common.toDate')}
                formik={props}
                format={MONTH_DATE_YEAR}
                onChange={(e) => {
                  setValues({
                    ...values,
                    dateType: undefined,
                    endDate: e?.format('YYYY-MM-DD'),
                  });
                }}
                disabledDate={(current) => {
                  return (
                    props?.values?.startDate &&
                    current <
                      moment(props.values.startDate, 'YYYY-MM-DD').startOf(
                        'day'
                      )
                  );
                }}
              />
            )}
          />

          {/* Payment Type */}
          <FormElementWrapper
            sm={12}
            md={6}
            xl={2}
            element={() => (
              <Select
                name="paymentType"
                placeholder="Payment Type"
                options={paymentTypeOptions}
                onItemChange={(val) => {
                  setValues((prev) => ({ ...prev, paymentType: val }));
                  props?.setFieldValue('paymentType', val);
                }}
                formik={props}
              />
            )}
          />

          {/* Deduction Status */}
          <FormElementWrapper
            sm={12}
            md={6}
            xl={2}
            element={() => (
              <Select
                name="deductionStatus"
                placeholder="Deduction Status"
                options={deductionStatusOptions}
                onItemChange={(val) => {
                  setValues((prev) => ({ ...prev, deductionStatus: val }));
                  props?.setFieldValue('deductionStatus', val);
                }}
                formik={props}
              />
            )}
          />

          {/* Action Buttons */}
          <FormElementWrapper
            sm={12}
            md={6}
            xl={2}
            element={() => (
              <div className="d-flex align-items-center">
                <CommonButton type="submit" className="btn btn-primary me-2">
                  {t('text.store.search')}
                </CommonButton>
                <CommonButton
                  onClick={() => {
                    onReset();
                    props?.resetForm({
                      values: {
                        startDate: '',
                        endDate: '',
                        dateType: undefined,
                        paymentType: '',
                        deductionStatus: '',
                      },
                    });
                  }}
                  className="btn btn-light"
                  type="button"
                >
                  {t('text.common.reset')}
                </CommonButton>
              </div>
            )}
          />
        </>
      )}
    </FormWrapper>
  );
}

export default RefundsAndDeductionsFilterForm;
